import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, X, Loader2 } from 'lucide-react';
import { searchProducts } from '../../services/productService';
import { getSearchCategorySuggestions } from '../../data/categoryHierarchy';

/**
 * Blended search autocomplete (Intent C).
 * - Category suggestions: instant, synchronous, from the static categoryHierarchy
 *   (ZERO database calls).
 * - Product suggestions: debounced 300ms, token-based DocumentDB-safe endpoint.
 * Categories render immediately; products append when the API responds.
 */
const SearchOverlay = ({ onClose }) => {
  const navigate = useNavigate();
  const [q, setQ] = useState('');
  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const inputRef = useRef(null);
  const debounceRef = useRef(null);
  const reqIdRef = useRef(0);

  const term = q.trim();

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  // Category suggestions — instant, in-memory.
  useEffect(() => {
    setCategories(getSearchCategorySuggestions(q, 6));
  }, [q]);

  // Product suggestions — debounced 300ms, stale-response guarded.
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (term.length < 2) {
      setProducts([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    debounceRef.current = setTimeout(async () => {
      const myReq = ++reqIdRef.current;
      try {
        const res = await searchProducts(term, { limit: 8 });
        if (myReq !== reqIdRef.current) return; // ignore stale response
        setProducts((res && res.data) || []);
      } catch {
        if (myReq === reqIdRef.current) setProducts([]);
      } finally {
        if (myReq === reqIdRef.current) setLoading(false);
      }
    }, 300);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [q, term]);

  const close = useCallback(() => onClose && onClose(), [onClose]);
  const goCategory = (route) => { navigate(route); close(); };
  const goProduct = (id) => { navigate(`/user/product/${id}`); close(); };
  const submitFullSearch = () => {
    if (!term) return;
    navigate(`/user/products?search=${encodeURIComponent(term)}`);
    close();
  };

  const onKeyDown = (e) => {
    if (e.key === 'Enter') { e.preventDefault(); submitFullSearch(); }
    else if (e.key === 'Escape') { close(); }
  };

  return (
    <div className="fixed inset-0 z-[60] bg-black/40" onClick={close}>
      <div
        className="bg-white w-full max-w-lg mx-auto shadow-xl animate-fade-in-up"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Search input row */}
        <div className="flex items-center gap-2 px-3 h-14 border-b border-gray-100">
          <Search className="w-5 h-5 text-gray-400 flex-shrink-0" strokeWidth={1.8} />
          <input
            ref={inputRef}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={onKeyDown}
            placeholder="Search for kurtas, sarees, jeans, brands…"
            className="flex-1 h-full outline-none text-[15px] text-gray-800 placeholder-gray-400 bg-transparent"
          />
          {q && (
            <button onClick={() => setQ('')} className="text-gray-400 p-1" aria-label="Clear">
              <X className="w-4 h-4" />
            </button>
          )}
          <button onClick={close} className="text-sm font-semibold text-orange-600 pl-1">
            Cancel
          </button>
        </div>

        {/* Results */}
        <div className="max-h-[72vh] overflow-y-auto">
          {term.length < 2 ? (
            <div className="p-6 text-center text-sm text-gray-400">
              Type at least 2 characters to search
            </div>
          ) : (
            <>
              {/* Category suggestions (instant) */}
              {categories.length > 0 && (
                <div className="py-2">
                  <p className="px-4 py-1 text-[11px] font-bold uppercase tracking-wider text-gray-400">
                    Categories
                  </p>
                  {categories.map((c, i) => (
                    <button
                      key={`cat-${i}`}
                      onClick={() => goCategory(c.route)}
                      className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-orange-50 text-left"
                    >
                      <Search className="w-4 h-4 text-gray-300 flex-shrink-0" strokeWidth={1.8} />
                      <span className="min-w-0">
                        <span className="block text-sm font-medium text-gray-800 truncate">{c.label}</span>
                        <span className="block text-[11px] text-gray-400 truncate">{c.path}</span>
                      </span>
                    </button>
                  ))}
                </div>
              )}

              {/* Product suggestions (debounced) */}
              <div className="py-2 border-t border-gray-50">
                <p className="px-4 py-1 text-[11px] font-bold uppercase tracking-wider text-gray-400 flex items-center gap-2">
                  Products
                  {loading && <Loader2 className="w-3 h-3 animate-spin text-orange-500" />}
                </p>
                {products.map((p) => (
                  <button
                    key={p._id}
                    onClick={() => goProduct(p._id)}
                    className="w-full flex items-center gap-3 px-4 py-2 hover:bg-orange-50 text-left"
                  >
                    {p.images && p.images[0] ? (
                      <img
                        src={p.images[0]}
                        alt={p.name}
                        className="w-10 h-12 object-cover object-top rounded-md bg-gray-100 flex-shrink-0"
                        onError={(e) => { e.currentTarget.style.visibility = 'hidden'; }}
                      />
                    ) : (
                      <div className="w-10 h-12 rounded-md bg-gray-100 flex-shrink-0" />
                    )}
                    <span className="min-w-0 flex-1">
                      <span className="block text-sm text-gray-800 truncate">{p.name}</span>
                      <span className="block text-[11px] text-gray-400 truncate">
                        {p.categoryPath || p.subCategory || ''}
                      </span>
                    </span>
                    <span className="text-sm font-semibold text-gray-900 flex-shrink-0">₹{p.zammerPrice}</span>
                  </button>
                ))}
                {!loading && products.length === 0 && (
                  <p className="px-4 py-3 text-sm text-gray-400">
                    No products found for “{term}”.
                  </p>
                )}
              </div>

              {/* See all results */}
              <button
                onClick={submitFullSearch}
                className="w-full text-center py-3 text-sm font-semibold text-orange-600 border-t border-gray-100 hover:bg-orange-50"
              >
                See all results for “{term}”
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default SearchOverlay;
